import JSZip from "jszip";

export interface EMDeltaExportMonth {
  label: string;
  assigned: number;
  capacity: number;
  customers: number;
  delta: number;
  deltaPercent: number;
  deltaRatio: number;
  ptoDays: number;
}

export interface EMDeltaExportRow {
  emName: string;
  regionName: string;
  months: EMDeltaExportMonth[];
}

interface EMDeltaExportColumn {
  field?:
    | "emName"
    | "regionName"
    | "capacity"
    | "assigned"
    | "customers"
    | "ptoDays";
  cellStyle?: "default" | "delta" | "percent";
  header: string;
  headerStyle?: "default" | "delta" | "percent";
  kind: "text" | "number" | "deltaFormula" | "percentFormula";
  monthIndex?: number;
  width: number;
}

export interface EMDeltaExportOptions {
  rows: EMDeltaExportRow[];
  showCustomers: boolean;
  showTimeOff: boolean;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function columnName(index: number): string {
  let name = "";
  let current = index;

  while (current > 0) {
    const remainder = (current - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    current = Math.floor((current - 1) / 26);
  }

  return name;
}

function sanitizeSheetName(name: string): string {
  const sanitized = name.replace(/[\\/?*\[\]:]/g, " ").trim();
  return (sanitized || "Export").substring(0, 31);
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function escapeCsvValue(value: string | number): string {
  const stringValue = String(value ?? "");
  if (
    stringValue.includes(",") ||
    stringValue.includes("\"") ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, "\"\"")}"`;
  }
  return stringValue;
}

function buildCsvHeaders(options: EMDeltaExportOptions): string[] {
  const headers = ["EM", "Region"];

  (options.rows[0]?.months || []).forEach((month) => {
    headers.push(`${month.label} Delta`);
    headers.push(`${month.label} Delta %`);
    if (options.showCustomers) {
      headers.push(`${month.label} Customers`);
    }
    if (options.showTimeOff) {
      headers.push(`${month.label} PTO Days`);
    }
  });

  return headers;
}

function buildCsvRow(
  row: EMDeltaExportRow,
  options: EMDeltaExportOptions
): Array<string | number> {
  const csvRow: Array<string | number> = [row.emName, row.regionName];

  row.months.forEach((month) => {
    csvRow.push(month.delta);
    csvRow.push(month.deltaPercent);
    if (options.showCustomers) {
      csvRow.push(month.customers);
    }
    if (options.showTimeOff) {
      csvRow.push(month.ptoDays);
    }
  });

  return csvRow;
}

function buildXlsxColumns(
  firstRow: EMDeltaExportRow | undefined,
  options: EMDeltaExportOptions
): EMDeltaExportColumn[] {
  const { showCustomers, showTimeOff } = options;
  const columns: EMDeltaExportColumn[] = [
    {
      field: "emName",
      header: "EM",
      headerStyle: "default",
      kind: "text",
      width: 24,
    },
    {
      field: "regionName",
      header: "Region",
      headerStyle: "default",
      kind: "text",
      width: 18,
    },
  ];

  (firstRow?.months || []).forEach((month, monthIndex) => {
    columns.push(
      {
        field: "capacity",
        header: `${month.label} Capacity`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 14,
      },
      {
        field: "assigned",
        header: `${month.label} Assigned`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 14,
      },
      {
        cellStyle: "delta",
        header: `${month.label} Delta`,
        headerStyle: "delta",
        kind: "deltaFormula",
        monthIndex,
        width: 12,
      },
      {
        cellStyle: "percent",
        header: `${month.label} Delta %`,
        headerStyle: "percent",
        kind: "percentFormula",
        monthIndex,
        width: 12,
      }
    );

    if (showCustomers) {
      columns.push({
        field: "customers",
        header: `${month.label} Customers`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 12,
      });
    }

    if (showTimeOff) {
      columns.push({
        field: "ptoDays",
        header: `${month.label} PTO Days`,
        headerStyle: "default",
        kind: "number",
        monthIndex,
        width: 12,
      });
    }
  });

  return columns;
}

function buildWorksheetXml(
  columns: EMDeltaExportColumn[],
  rows: EMDeltaExportRow[]
): string {
  const monthSupportRefs = columns.reduce((refs, column, index) => {
    if (column.kind === "number" && column.monthIndex !== undefined) {
      const monthRefs = refs[column.monthIndex] || {};
      if (column.field === "capacity") {
        monthRefs.capacity = columnName(index + 1);
      }
      if (column.field === "assigned") {
        monthRefs.assigned = columnName(index + 1);
      }
      refs[column.monthIndex] = monthRefs;
    }
    return refs;
  }, {} as { [monthIndex: number]: { capacity?: string; assigned?: string } });

  const headerCells = columns
    .map((column, index) => {
      const ref = `${columnName(index + 1)}1`;
      const headerStyleId =
        column.headerStyle === "delta"
          ? 3
          : column.headerStyle === "percent"
            ? 4
            : 1;
      return `<c r="${ref}" t="inlineStr" s="${headerStyleId}"><is><t xml:space="preserve">${escapeXml(
        column.header
      )}</t></is></c>`;
    })
    .join("");

  const bodyRows = rows
    .map((row, rowIndex) => {
      const excelRow = rowIndex + 2;
      const cells = columns
        .map((column, columnIndex) => {
          const ref = `${columnName(columnIndex + 1)}${excelRow}`;

          if (column.kind === "text") {
            const value = row[column.field as "emName" | "regionName"];
            return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
              value
            )}</t></is></c>`;
          }

          const month = row.months[column.monthIndex || 0];

          if (column.kind === "number") {
            const value =
              month[
                column.field as "capacity" | "assigned" | "customers" | "ptoDays"
              ];
            const cellStyleId =
              column.cellStyle === "delta"
                ? 5
                : column.cellStyle === "percent"
                  ? 6
                  : 0;
            return `<c r="${ref}"${
              cellStyleId ? ` s="${cellStyleId}"` : ""
            }><v>${value}</v></c>`;
          }

          const monthRefs = monthSupportRefs[column.monthIndex || 0] || {};
          const capacityRef = `${monthRefs.capacity}${excelRow}`;
          const assignedRef = `${monthRefs.assigned}${excelRow}`;

          if (column.kind === "deltaFormula") {
            return `<c r="${ref}" s="5"><f>${capacityRef}-${assignedRef}</f><v>${month.delta}</v></c>`;
          }

          return `<c r="${ref}" s="6"><f>IF(${capacityRef}=0,0,${assignedRef}/${capacityRef})</f><v>${month.deltaRatio}</v></c>`;
        })
        .join("");

      return `<row r="${excelRow}">${cells}</row>`;
    })
    .join("");

  const colsXml = columns
    .map(
      (column, index) =>
        `<col min="${index + 1}" max="${index + 1}" width="${column.width}" customWidth="1"/>`
    )
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <cols>${colsXml}</cols>
  <sheetData>
    <row r="1">${headerCells}</row>
    ${bodyRows}
  </sheetData>
</worksheet>`;
}

function buildWorkbookXml(sheetName: string): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeXml(sheetName)}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;
}

function buildWorkbookRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

function buildStylesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="1">
    <numFmt numFmtId="164" formatCode="0.0%"/>
  </numFmts>
  <fonts count="2">
    <font>
      <sz val="11"/>
      <name val="Calibri"/>
      <family val="2"/>
    </font>
    <font>
      <b/>
      <sz val="11"/>
      <name val="Calibri"/>
      <family val="2"/>
    </font>
  </fonts>
  <fills count="6">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFC5CCF0"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFD7DDF4"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFE0E5F8"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
    <fill>
      <patternFill patternType="solid">
        <fgColor rgb="FFECEFFA"/>
        <bgColor indexed="64"/>
      </patternFill>
    </fill>
  </fills>
  <borders count="1">
    <border>
      <left/>
      <right/>
      <top/>
      <bottom/>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0"/>
  </cellStyleXfs>
  <cellXfs count="7">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="1" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="0" xfId="0" applyFill="1"/>
    <xf numFmtId="164" fontId="0" fillId="5" borderId="0" xfId="0" applyNumberFormat="1" applyFill="1"/>
  </cellXfs>
  <cellStyles count="1">
    <cellStyle name="Normal" xfId="0" builtinId="0"/>
  </cellStyles>
</styleSheet>`;
}

function buildContentTypesXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;
}

function buildRootRelsXml(): string {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
}

export async function downloadEmDeltaXlsx(
  filename: string,
  sheetName: string,
  options: EMDeltaExportOptions
): Promise<void> {
  const columns = buildXlsxColumns(options.rows[0], options);
  const zip = new JSZip();
  const sanitizedSheetName = sanitizeSheetName(sheetName);

  zip.file("[Content_Types].xml", buildContentTypesXml());
  zip.folder("_rels")?.file(".rels", buildRootRelsXml());
  zip.folder("xl")?.file("workbook.xml", buildWorkbookXml(sanitizedSheetName));
  zip.folder("xl")?.folder("_rels")?.file("workbook.xml.rels", buildWorkbookRelsXml());
  zip.folder("xl")?.file("styles.xml", buildStylesXml());
  zip
    .folder("xl")
    ?.folder("worksheets")
    ?.file("sheet1.xml", buildWorksheetXml(columns, options.rows));

  const blob = await zip.generateAsync({
    mimeType:
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    type: "blob",
  });

  downloadBlob(filename, blob);
}

export function downloadEmDeltaCsv(
  filename: string,
  options: EMDeltaExportOptions
): void {
  const headers = buildCsvHeaders(options);
  const rows = options.rows.map((row) => buildCsvRow(row, options));
  const csv = [headers, ...rows]
    .map((row) => row.map((value) => escapeCsvValue(value)).join(","))
    .join("\n");

  downloadBlob(
    filename,
    new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    })
  );
}
